import { useEffect, useState } from 'react'
import type { Track } from '../types/music'
import { readStore, writeStore } from '../utils/storage'

export type LyricsStatus = 'idle' | 'loading' | 'found' | 'missing' | 'error'

interface LyricsState {
  status: LyricsStatus
  lines: string[]
}

/** Strip remaster/feature clutter that stops the lyrics lookup from matching. */
function cleanTitle(title: string): string {
  return title
    .replace(/\([^)]*\)/g, '')
    .replace(/\[[^\]]*\]/g, '')
    .replace(/-\s*(remaster|remastered|live|radio edit|single version).*/i, '')
    .replace(/\s*(feat\.|ft\.).*/i, '')
    .trim()
}

function cleanArtist(artist: string): string {
  return artist.split(/,|&|\bfeat\.|\bft\./i)[0].trim()
}

/**
 * Looks up lyrics for the playing track from the keyless lyrics.ovh API and
 * caches hits locally, since instrumentals and obscure releases often miss.
 */
export function useLyrics(track: Track | null): LyricsState {
  const [state, setState] = useState<LyricsState>({ status: 'idle', lines: [] })

  useEffect(() => {
    if (!track) {
      setState({ status: 'idle', lines: [] })
      return
    }
    const artist = cleanArtist(track.artist)
    const title = cleanTitle(track.title)
    const cacheKey = `lyrics:${artist}|${title}`.toLowerCase()

    const cached = readStore<string[] | 'missing' | null>(cacheKey, null)
    if (cached === 'missing') {
      setState({ status: 'missing', lines: [] })
      return
    }
    if (Array.isArray(cached)) {
      setState({ status: 'found', lines: cached })
      return
    }

    const controller = new AbortController()
    setState({ status: 'loading', lines: [] })

    void (async () => {
      try {
        const res = await fetch(
          `https://api.lyrics.ovh/v1/${encodeURIComponent(artist)}/${encodeURIComponent(title)}`,
          { signal: controller.signal },
        )
        if (res.status === 404) {
          writeStore(cacheKey, 'missing')
          setState({ status: 'missing', lines: [] })
          return
        }
        if (!res.ok) throw new Error(`Lyrics lookup failed (${res.status})`)
        const json = (await res.json()) as { lyrics?: string }
        const lines = (json.lyrics ?? '')
          .replace(/\r/g, '')
          .split('\n')
          .map((line) => line.trim())
        const meaningful = lines.filter((line) => line.length > 0)
        if (meaningful.length === 0) {
          writeStore(cacheKey, 'missing')
          setState({ status: 'missing', lines: [] })
          return
        }
        writeStore(cacheKey, lines)
        setState({ status: 'found', lines })
      } catch (err) {
        if ((err as Error).name === 'AbortError') return
        setState({ status: 'error', lines: [] })
      }
    })()

    return () => controller.abort()
  }, [track])

  return state
}
